import { FC, useRef, useState } from 'react';
import { getIn, useFormikContext } from 'formik';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AddAPhoto as AddAPhotoIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useToastr } from '@farmshare/ui-components';
import { useGenerateUploadUrlMutation } from 'lib/graphql';

// Accepted image MIME types
const ACCEPTED_TYPES = 'image/jpeg,image/png,image/webp,image/gif';

// Max file size: 5 MB
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;

// S3 key prefix for cutsheet images
const KEY_PREFIX = 'cutsheet-images';

/**
 * Generates a unique S3 key for an uploaded image file.
 * Format: cutsheet-images/{capabilityId}/{timestamp}-{random}.{ext}
 * @param file - The file to generate a key for
 * @param capabilityId - Capability set ID used to group images in S3
 * @returns S3 object key string
 */
function buildKey(file: File, capabilityId: string): string {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const randomSuffix = Math.random().toString(36).slice(2, 9);
  return `${KEY_PREFIX}/${capabilityId}/${Date.now()}-${randomSuffix}.${ext}`;
}

interface ImageUploadInputProps {
  /** Formik field name that holds the image URL (defaults to "imageUrl") */
  nameOverride?: string;
  label?: string;
  /** Capability set ID — used to group images under a common S3 prefix */
  capabilityId: string;
  /**
   * When true, renders as a circular Avatar instead of the standard
   * preview+button layout. Clicking the avatar (or the upload icon when empty)
   * triggers the file picker. A delete icon appears on hover when an image is set.
   */
  avatar?: boolean;
}

/**
 * Image upload field for cutsheet forms.
 *
 * Reads/writes the image URL through Formik context. On file selection it
 * requests a presigned S3 PUT URL, uploads the file directly from the browser,
 * then stores the resulting CDN URL in the Formik field.
 * @param props - Component props
 * @param props.nameOverride - Formik field name that holds the image URL
 * @param props.label - Label shown above the control
 * @param props.capabilityId - Capability set ID used to group images in S3
 * @param props.avatar
 * @returns Image upload input component
 */
export const ImageUploadInput: FC<ImageUploadInputProps> = ({
  nameOverride = 'imageUrl',
  label = 'Image',
  capabilityId,
  avatar = false,
}) => {
  const { values, setFieldValue } = useFormikContext<Record<string, string>>();
  const { push } = useToastr();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const [generateUploadUrl] = useGenerateUploadUrlMutation();

  const currentUrl: string = getIn(values, nameOverride) ?? '';

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-selected after a clear
    if (fileInputRef.current) fileInputRef.current.value = '';
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      push({
        title: 'File too large',
        body: 'Images must be under 5 MB.',
        bg: 'danger',
      });
      return;
    }

    setUploading(true);
    try {
      const key = buildKey(file, capabilityId);

      const { data } = await generateUploadUrl({
        variables: { key, contentType: file.type },
      });

      if (!data?.generateUploadUrl) {
        throw new Error('Failed to get upload URL from server.');
      }

      const { uploadUrl, cdnUrl } = data.generateUploadUrl;

      // Upload directly to S3 — no binary data sent through the API server
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type },
      });

      if (!uploadResponse.ok) {
        throw new Error(`Upload failed with status ${uploadResponse.status}.`);
      }

      await setFieldValue(nameOverride, cdnUrl);
      push({
        title: 'Image uploaded',
        body: 'Image saved successfully.',
        bg: 'success',
      });
    } catch (err) {
      push({
        title: 'Upload failed',
        body: err instanceof Error ? err.message : 'An unknown error occurred.',
        bg: 'danger',
      });
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setFieldValue(nameOverride, '');
  };

  const hiddenInput = (
    <input
      ref={fileInputRef}
      type="file"
      accept={ACCEPTED_TYPES}
      style={{ display: 'none' }}
      onChange={handleFileChange}
    />
  );

  if (avatar) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        {hiddenInput}
        <Tooltip title={currentUrl ? 'Change image' : 'Upload image'}>
          <Box
            sx={{
              position: 'relative',
              cursor: 'pointer',
              display: 'inline-flex',
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <Avatar
              src={currentUrl || undefined}
              sx={{ width: 92, height: 92, bgcolor: 'action.hover' }}
              variant="rounded"
            >
              {uploading ? (
                <CircularProgress size={24} />
              ) : (
                !currentUrl && (
                  <AddAPhotoIcon sx={{ color: 'text.secondary' }} />
                )
              )}
            </Avatar>
            {currentUrl && !uploading && (
              <IconButton
                size="small"
                aria-label="Remove image"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
                sx={{
                  position: 'absolute',
                  top: -6,
                  right: -6,
                  width: 20,
                  height: 20,
                  bgcolor: 'background.paper',
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    bgcolor: 'error.light',
                    color: 'error.contrastText',
                  },
                }}
              >
                <DeleteIcon sx={{ fontSize: 12 }} />
              </IconButton>
            )}
          </Box>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2" color="text.secondary">
        {label}
      </Typography>

      {/* Preview */}
      {currentUrl && (
        <Box
          sx={{
            position: 'relative',
            display: 'inline-flex',
            alignSelf: 'flex-start',
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={currentUrl}
            alt="Image preview"
            sx={{
              maxHeight: 120,
              maxWidth: 240,
              display: 'block',
              objectFit: 'contain',
            }}
          />
          <IconButton
            size="small"
            aria-label="Remove image"
            onClick={handleRemove}
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              bgcolor: 'background.paper',
              '&:hover': {
                bgcolor: 'error.light',
                color: 'error.contrastText',
              },
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      {/* Upload button */}
      <Box>
        {hiddenInput}
        <Button
          variant="outlined"
          size="small"
          startIcon={
            uploading ? <CircularProgress size={14} /> : <CloudUploadIcon />
          }
          disabled={uploading}
          onClick={() => fileInputRef.current?.click()}
        >
          {uploading
            ? 'Uploading…'
            : currentUrl
              ? 'Replace Image'
              : 'Upload Image'}
        </Button>
      </Box>
    </Stack>
  );
};
